import { get } from 'lodash/fp';
import { ComboType, OptionKind, OptionType } from '../../../../api/types';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { type OptionBlockFormValues } from './utils';

const validator = validators.compose(
    validators.forEach(
        'optionsBlocks',
        validators.compose(
            validators.requiredString('type'),
            validators.requiredBoolean('optionsBlockInput.isActive'),
            validators.requiredBoolean('optionsBlockInput.financing'),
            validators.requiredString('optionsBlockInput.sectionTitle.defaultValue'),
            validators.requiredString('optionsBlockInput.optionSettings.type'),

            validators.nest(
                'optionsBlockInput.optionSettings',
                validators.compose(
                    // Single select
                    validators.only(
                        (values: OptionBlockFormValues, errors, { prefix }) =>
                            get(prefix, values)?.type === OptionKind.SingleSelect,
                        validators.forEach(
                            'singleSelectOptionSettingsInput.options',
                            validators.compose(
                                validators.requiredString('label.defaultValue'),
                                validators.requiredString('type'),
                                validators.only(
                                    (values: OptionBlockFormValues, errors, { prefix }) =>
                                        get(prefix, values)?.type === OptionType.Description,
                                    validators.requiredString('valueText.defaultValue')
                                ),
                                validators.only(
                                    (values: OptionBlockFormValues, errors, { prefix }) =>
                                        get(prefix, values)?.type === OptionType.Price,
                                    validators.requiredNumber('valuePrice')
                                )
                            )
                        )
                    ),

                    // Multi select
                    validators.only(
                        (values: OptionBlockFormValues, errors, { prefix }) =>
                            get(prefix, values)?.type === OptionKind.MultiSelect,
                        validators.forEach(
                            'multiSelectOptionSettingsInput.options',
                            validators.compose(
                                validators.requiredString('label.defaultValue'),
                                validators.requiredString('type'),
                                validators.only(
                                    (values: OptionBlockFormValues, errors, { prefix }) =>
                                        get(prefix, values)?.type === OptionType.Description,
                                    validators.requiredString('valueText.defaultValue')
                                ),
                                validators.only(
                                    (values: OptionBlockFormValues, errors, { prefix }) =>
                                        get(prefix, values)?.type === OptionType.Price,
                                    validators.requiredNumber('valuePrice')
                                )
                            )
                        )
                    ),

                    // Combo
                    validators.only(
                        (values: OptionBlockFormValues, errors, { prefix }) =>
                            get(prefix, values)?.type === OptionKind.Combo,
                        validators.compose(
                            validators.requiredNumber('comboOptionSettingsInput.price'),
                            validators.forEach(
                                'comboOptionSettingsInput.options',
                                validators.compose(
                                    validators.requiredString('label.defaultValue'),
                                    validators.requiredString('type'),
                                    validators.only(
                                        (values: OptionBlockFormValues, errors, { prefix }) =>
                                            get(prefix, values)?.type === ComboType.Dropdown,
                                        validators.compose(
                                            validators.requiredValue('descriptions'),
                                            validators.forEach(
                                                'descriptions',
                                                validators.compose(validators.requiredString('defaultValue'))
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            )
        )
    )
);

const useOptionsBlockValidator = () => useValidator(validator);

export default useOptionsBlockValidator;
